import React from 'react';
import { Typography, Card as AntCard, Button } from 'antd';
import { Sun, Battery, Thermometer, Droplets, Wind, Camera, Smartphone, Globe, Zap, CheckCircle } from 'lucide-react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import HeroImg4 from '@/assets/images/RAW images/Qodearis_img4.png';
import HeroImg6 from '@/assets/images/RAW images/Qodearis_img6.png';
import HeroImg8 from '@/assets/images/RAW images/Qodearis_img8.png';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';

// Type workaround for Card component
const Card = AntCard as any;

const { Title, Text, Paragraph } = Typography;

export const Product: React.FC = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-emerald-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
      <Helmet>
        <title>DhartiNetra Farm Rakshak - Smart Farm Monitoring System</title>
        <meta name="description" content="DhartiNetra is a solar-powered IoT device for 24x7 farm monitoring. Get real-time data on soil moisture, NPK, weather, and more." />
        <meta name="keywords" content="DhartiNetra, farm rakshak, soil sensors, NPK sensor, smart agriculture device" />
      </Helmet>
      <Navbar />
      <main id="main-content" className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Hero Section */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <Title level={1} className="text-5xl md:text-6xl font-bold mb-6">
                DhartiNetra
                <span className="bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent"> Farm Rakshak</span>
              </Title>
              <Paragraph className="text-xl text-gray-600 dark:text-gray-300 mb-6">
                Namaskar Farmer Brothers and Sisters,<br />
                A modern, solar-powered farm device that keeps watch over your field day and night.
              </Paragraph>
              <Text className="text-lg font-semibold text-green-700 dark:text-green-400 block mb-8">
                With DhartiNetra as your Kheti Saathi, farming becomes easier, safer, and more profitable.
              </Text>
              <div className="flex gap-4 flex-wrap">
                <Button
                  type="primary"
                  size="large"
                  className="bg-green-600 hover:bg-green-700"
                >
                  Request a Demo
                </Button>
                <Button
                  size="large"
                  href="/contact"
                >
                  Contact Sales
                </Button>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative"
            >
              <img 
                src={HeroImg4} 
                alt="DhartiNetra Smart Farming Device" 
                className="rounded-2xl shadow-2xl w-full"
              />
            </motion.div>
          </div>

          {/* Product Diagram */}
          <div className="mb-16 bg-gradient-to-br from-green-50 to-emerald-50 dark:from-gray-800 dark:to-gray-700 rounded-2xl p-8">
            <div className="text-center mb-8">
              <Title level={2} className="mb-4">Product Architecture</Title>
              <Text className="text-lg text-gray-600 dark:text-gray-300">
                Complete IoT ecosystem for smart farming
              </Text>
            </div>
            <img 
              src={HeroImg6} 
              alt="DhartiNetra Product Diagram and Architecture" 
              className="rounded-xl shadow-lg w-full max-w-5xl mx-auto"
            />
          </div>

          {/* Key Components */}
          <section className="mb-16">
            <div className="text-center mb-12">
              <Title level={2} className="mb-4">Key Components</Title>
              <Text className="text-lg text-gray-600 dark:text-gray-300">
                Designed for Indian field conditions, with minimal maintenance
              </Text>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
              >
                <Card className="h-full shadow-lg hover:shadow-xl transition-shadow text-center">
                  <Sun className="w-16 h-16 text-yellow-600 mx-auto mb-4" aria-hidden="true" />
                  <Title level={4} className="mb-2">Solar Panel</Title>
                  <Text className="text-gray-600 dark:text-gray-300">
                    Eco-friendly power source for continuous operation
                  </Text>
                </Card>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                <Card className="h-full shadow-lg hover:shadow-xl transition-shadow text-center">
                  <Battery className="w-16 h-16 text-green-600 mx-auto mb-4" aria-hidden="true" />
                  <Title level={4} className="mb-2">DC Battery</Title>
                  <Text className="text-gray-600 dark:text-gray-300">
                    Backup power for 24×7 monitoring
                  </Text>
                </Card>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.3 }}
              >
                <Card className="h-full shadow-lg hover:shadow-xl transition-shadow text-center">
                  <Thermometer className="w-16 h-16 text-red-600 mx-auto mb-4" aria-hidden="true" />
                  <Title level={4} className="mb-2">Environmental Sensors</Title>
                  <Text className="text-gray-600 dark:text-gray-300">
                    Temperature, Humidity, NPK monitoring
                  </Text>
                </Card>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.4 }}
              >
                <Card className="h-full shadow-lg hover:shadow-xl transition-shadow text-center">
                  <Wind className="w-16 h-16 text-blue-600 mx-auto mb-4" aria-hidden="true" />
                  <Title level={4} className="mb-2">Wind Antenna</Title>
                  <Text className="text-gray-600 dark:text-gray-300">
                    Wind speed and direction tracking
                  </Text>
                </Card>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.5 }}
              >
                <Card className="h-full shadow-lg hover:shadow-xl transition-shadow text-center">
                  <Zap className="w-16 h-16 text-purple-600 mx-auto mb-4" aria-hidden="true" />
                  <Title level={4} className="mb-2">Lux Meter</Title>
                  <Text className="text-gray-600 dark:text-gray-300">
                    Light intensity measurement
                  </Text>
                </Card>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.6 }}
              >
                <Card className="h-full shadow-lg hover:shadow-xl transition-shadow text-center">
                  <Camera className="w-16 h-16 text-indigo-600 mx-auto mb-4" aria-hidden="true" />
                  <Title level={4} className="mb-2">Bullet Camera</Title>
                  <Text className="text-gray-600 dark:text-gray-300">
                    Remote crop monitoring and safety
                  </Text>
                </Card>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.7 }}
              >
                <Card className="h-full shadow-lg hover:shadow-xl transition-shadow text-center">
                  <Droplets className="w-16 h-16 text-cyan-600 mx-auto mb-4" aria-hidden="true" />
                  <Title level={4} className="mb-2">Soil Sensors</Title>
                  <Text className="text-gray-600 dark:text-gray-300">
                    Moisture and nutrient analysis
                  </Text>
                </Card>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.8 }}
              >
                <Card className="h-full shadow-lg hover:shadow-xl transition-shadow text-center">
                  <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-4" aria-hidden="true" />
                  <Title level={4} className="mb-2">Rugged Pole</Title>
                  <Text className="text-gray-600 dark:text-gray-300">
                    Durable mounting for all conditions
                  </Text>
                </Card>
              </motion.div>
            </div>
          </section>

          {/* Mobile App Section */}
          <section className="mb-16 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-2xl p-8 md:p-12 text-white">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
              <div>
                <Smartphone className="w-16 h-16 mb-6" aria-hidden="true" />
                <Title level={2} className="text-white mb-4">Mobile App – Farming Made Simple</Title>
                <Paragraph className="text-xl text-white/90 mb-6">
                  Our intuitive mobile app puts the power of agricultural intelligence directly in farmers' hands.
                </Paragraph>
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-6 h-6 flex-shrink-0 mt-1" aria-hidden="true" />
                    <Text className="text-white">Real-time Alerts – irrigation, pest threats, harvesting windows</Text>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-6 h-6 flex-shrink-0 mt-1" aria-hidden="true" />
                    <Text className="text-white">Easy Data Visualization – simple graphs & indicators</Text>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-6 h-6 flex-shrink-0 mt-1" aria-hidden="true" />
                    <Text className="text-white">Multi-Language Support – built for regional accessibility</Text>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-6 h-6 flex-shrink-0 mt-1" aria-hidden="true" />
                    <Text className="text-white">Offline Mode – works even with limited connectivity</Text>
                  </div>
                </div>
                <Paragraph className="text-lg text-white/90 mt-6 font-semibold">
                  No technical knowledge required.
                </Paragraph>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8 text-center">
                <Smartphone className="w-32 h-32 mx-auto mb-4 text-white/80" aria-hidden="true" />
                <Text className="text-white text-lg">
                  Available on Android & iOS
                </Text>
              </div>
            </div>
          </section>

          {/* Web Dashboard Section */}
          <section className="mb-16">
            <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 md:p-12 shadow-xl">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
                <div className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-gray-700 dark:to-gray-600 rounded-xl p-8 text-center">
                  <Globe className="w-32 h-32 mx-auto mb-4 text-green-600 dark:text-green-400" aria-hidden="true" />
                  <Text className="text-lg font-semibold">
                    Web Dashboard & AI Chatbot
                  </Text>
                </div>
                <div>
                  <Title level={2} className="mb-4">For Agronomists & Partners</Title>
                  <Paragraph className="text-lg text-gray-600 dark:text-gray-300 mb-6">
                    Centralized platform for FPOs, cooperatives, and agribusiness partners
                  </Paragraph>
                  <div className="space-y-3">
                    <div className="flex items-start gap-3">
                      <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" aria-hidden="true" />
                      <Text className="text-gray-700 dark:text-gray-300">Centralized dashboards for multi-farm monitoring</Text>
                    </div>
                    <div className="flex items-start gap-3">
                      <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" aria-hidden="true" />
                      <Text className="text-gray-700 dark:text-gray-300">Historical trend analysis and insights</Text>
                    </div>
                    <div className="flex items-start gap-3">
                      <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" aria-hidden="true" />
                      <Text className="text-gray-700 dark:text-gray-300">AI-powered multilingual chatbot for instant guidance</Text>
                    </div>
                    <div className="flex items-start gap-3">
                      <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" aria-hidden="true" />
                      <Text className="text-gray-700 dark:text-gray-300">Export reports and analytics</Text>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Benefits Section */}
          <section className="mb-16">
            <div className="text-center mb-12">
              <Title level={2} className="mb-4">Benefits for Farmers</Title>
              <Text className="text-lg text-gray-600 dark:text-gray-300">
                Real impact on farming operations and livelihoods
              </Text>
            </div>

            {/* Benefits Diagram */}
            <div className="mb-12 bg-gradient-to-br from-green-50 to-emerald-50 dark:from-gray-800 dark:to-gray-700 rounded-2xl p-8">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                <img 
                  src={HeroImg8} 
                  alt="DhartiNetra Product Benefits Diagram" 
                  className="rounded-xl shadow-lg w-full max-w-6xl mx-auto"
                />
              </motion.div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                { title: 'Increased Yields', desc: 'Healthier crops through precise actions and timely interventions', icon: '📈' },
                { title: 'Reduced Costs', desc: 'Optimized water, fertilizer, and pesticide usage', icon: '💰' },
                { title: 'Risk Mitigation', desc: 'Early warnings prevent major losses from weather and pests', icon: '🛡️' },
                { title: 'Improved Sustainability', desc: 'Eco-friendly resource management and soil health', icon: '🌱' },
                { title: 'Empowerment', desc: 'Data-driven decision-making instead of guesswork', icon: '💪' },
                { title: 'Training & Development', desc: 'Skill building through insights and recommendations', icon: '📚' },
              ].map((benefit, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.3, delay: index * 0.1 }}
                >
                  <Card className="h-full shadow-lg hover:shadow-xl transition-shadow">
                    <div className="text-4xl mb-4">{benefit.icon}</div>
                    <Title level={4} className="mb-2">{benefit.title}</Title>
                    <Text className="text-gray-600 dark:text-gray-300">{benefit.desc}</Text>
                  </Card>
                </motion.div>
              ))}
            </div>
          </section>

          {/* CTA Section */}
          <section>
            <Card className="bg-gradient-to-r from-green-600 to-emerald-600 text-white border-0 shadow-xl">
              <div className="text-center py-8">
                <Title level={2} className="text-white mb-4">Ready to Transform Your Farm?</Title>
                <Paragraph className="text-xl text-white/90 max-w-3xl mx-auto mb-6">
                  Join thousands of farmers who are already using DhartiNetra to increase yields, 
                  reduce costs, and farm with confidence.
                </Paragraph>
                <div className="flex gap-4 justify-center flex-wrap">
                  <button className="bg-white text-green-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors">
                    Request a Demo
                  </button>
                  <button className="border-2 border-white text-white px-8 py-3 rounded-lg font-semibold hover:bg-white/10 transition-colors">
                    Download Brochure
                  </button>
                </div>
              </div>
            </Card>
          </section>
        </div>
      </main>
      <Footer />
    </div>
  );
};
